import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { NotificationKind, NotificationKindLabel, RoleKind } from '@core/models/brijjsports.enums';
import { AppNotification } from '@core/models/brijjsports.models';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';

@Component({
  selector: 'app-notifications-bell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, MatBadgeModule, MatButtonModule, MatDividerModule, MatIconModule, MatMenuModule],
  templateUrl: './notifications-bell.component.html',
  styleUrl: './notifications-bell.component.scss'
})
export class NotificationsBellComponent {
  private readonly state = inject(BrijjSportsStateService);

  protected readonly kindLabel = NotificationKindLabel;

  protected readonly notifications = computed<AppNotification[]>(() => {
    const role = this.state.currentRole();
    const ctx = this.state.currentContext();
    const userId =
      role === RoleKind.Coach
        ? ctx.coachId
        : role === RoleKind.Athlete
          ? ctx.athleteId
          : role === RoleKind.Parent
            ? ctx.parentId
            : null;
    return this.state.notificationsFor(role, userId).slice(0, 25);
  });

  protected readonly unreadCount = computed(() => this.notifications().filter(n => !n.read).length);

  protected iconFor(kind: NotificationKind): string {
    switch (kind) {
      case NotificationKind.SessionStarted:
        return 'play_circle';
      case NotificationKind.SessionEnded:
        return 'flag';
      case NotificationKind.ScoreUpdate:
        return 'sports_score';
      case NotificationKind.CoachNoteAdded:
        return 'sticky_note_2';
      case NotificationKind.GoalMilestone:
        return 'emoji_events';
      case NotificationKind.EquipmentChange:
        return 'build';
      default:
        return 'notifications';
    }
  }

  protected open(n: AppNotification): void {
    if (!n.read) this.state.markNotificationRead(n.id);
  }

  protected markAllRead(event: Event): void {
    event.stopPropagation();
    this.state.markAllNotificationsRead();
  }

  protected clearAll(event: Event): void {
    event.stopPropagation();
    this.state.clearNotifications();
  }
}
